import { useState } from "react";
import { clinicInfo } from "../data";

export default function Contact() {
  const [formData, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    website_url: "", // Honeypot field
  });

  const [status, setStatus] = useState("idle"); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setForm({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("success");
        setForm({
          name: "",
          phone: "",
          email: "",
          message: "",
          website_url: "",
        });
      } else {
        const errData = await response.json();
        throw new Error(errData.message || "Der skete en fejl ved afsendelse.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
      setErrorMessage(error.message || "Der skete en fejl. Prøv venligst igen senere.");
    }
  };

  return (
    <div className="space-y-24 pb-24">
      {/* Header Banner */}
      <section className="bg-gradient-to-b from-brand-100/50 to-white py-16 text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-4">
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-brand-950">
            Kontakt os
          </h1>
          <p className="text-lg text-brand-800 max-w-2xl mx-auto">
            Har du spørgsmål, eller ønsker du at bestille en tid? Vi sidder klar til at hjælpe dig videre.
          </p>
        </div>
      </section>

      {/* Main Contact Grid */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Contact Info (5 cols) */}
          <div className="lg:col-span-5 space-y-8 text-left">
            <div className="rounded-3xl bg-white border border-brand-100 p-8 sm:p-10 shadow-xs space-y-8">
              <h2 className="text-2xl font-bold font-display text-brand-950 border-b border-brand-100 pb-4">
                Klinikinformation
              </h2>

              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                    <i className="fa-light fa-location-dot text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-900 text-sm">Adresse</h3>
                    <p className="text-sm text-brand-700 mt-1">{clinicInfo.address}</p>
                    <p className="text-xs text-brand-500 mt-1">1. sal i det gamle sygehus. Elevator og gratis parkering.</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                    <i className="fa-light fa-phone text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-900 text-sm">Telefon</h3>
                    <p className="text-sm text-brand-700 mt-1">
                      <a href={`tel:${clinicInfo.phone}`} className="hover:underline whitespace-nowrap text-brand-900 font-semibold">
                        <span className="whitespace-nowrap">{clinicInfo.phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4")}</span>
                      </a>
                    </p>
                    <p className="text-xs text-brand-500 mt-1">Telefontid: {clinicInfo.telefontider}</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                    <i className="fa-light fa-envelope text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-900 text-sm">E-mail</h3>
                    <p className="text-sm text-brand-700 mt-1">
                      <a href={`mailto:${clinicInfo.email}`} className="hover:underline whitespace-nowrap text-brand-900 font-semibold">
                        <span className="whitespace-nowrap">{clinicInfo.email}</span>
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Emergency Vagtordning */}
              <div className="rounded-2xl bg-brand-50 p-6 border border-brand-100/50 space-y-3">
                <h3 className="font-bold text-brand-900 text-sm flex items-center gap-2">
                  <i className="fa-light fa-circle-exclamation text-brand-600 text-lg" />
                  Akut vagtordning
                </h3>
                <p className="text-xs text-brand-700 leading-relaxed">
                  {clinicInfo.vagtordning.text}
                </p>
                <p className="text-xs text-brand-800 font-semibold">
                  Tlf: <a href={`tel:${clinicInfo.vagtordning.phone.replace(/\s+/g, "")}`} className="hover:underline whitespace-nowrap">
                    <span className="whitespace-nowrap">{clinicInfo.vagtordning.phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4")}</span>
                  </a>
                </p>
                <p className="text-[11px] text-brand-500">
                  {clinicInfo.vagtordning.hours}. {clinicInfo.vagtordning.fee}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form (7 cols) */}
          <div className="lg:col-span-7 text-left">
            <div className="rounded-3xl bg-white border border-brand-100 p-8 sm:p-10 shadow-xs space-y-6">
              <h2 className="text-2xl font-bold font-display text-brand-950 border-b border-brand-100 pb-4">
                Send os en besked
              </h2>

              {status === "success" ? (
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-center space-y-4 animate-in fade-in duration-300">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <i className="fa-solid fa-check text-xl" />
                  </div>
                  <h3 className="text-lg font-bold text-emerald-900 font-display">Beskeden er sendt!</h3>
                  <p className="text-sm text-emerald-700 max-w-md mx-auto">
                    Mange tak for din henvendelse. Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot Anti-Spam Field */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Udfyld ikke dette felt</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-semibold text-brand-900">
                        Navn <span className="text-brand-600">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-brand-200 bg-brand-50/30 px-4 py-3 text-sm text-brand-900 placeholder-brand-400 focus:border-brand-500 focus:bg-white focus:outline-hidden transition-colors"
                        placeholder="Dit fulde navn"
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <label htmlFor="phone" className="text-sm font-semibold text-brand-900">
                        Telefonnummer <span className="text-brand-600">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-brand-200 bg-brand-50/30 px-4 py-3 text-sm text-brand-900 placeholder-brand-400 focus:border-brand-500 focus:bg-white focus:outline-hidden transition-colors"
                        placeholder="Dit telefonnummer"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-semibold text-brand-900">
                      E-mail <span className="text-brand-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-brand-200 bg-brand-50/30 px-4 py-3 text-sm text-brand-900 placeholder-brand-400 focus:border-brand-500 focus:bg-white focus:outline-hidden transition-colors"
                      placeholder="Din e-mailadresse"
                    />
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-semibold text-brand-900">
                      Beskrivelse <span className="text-brand-600">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-brand-200 bg-brand-50/30 px-4 py-3 text-sm text-brand-900 placeholder-brand-400 focus:border-brand-500 focus:bg-white focus:outline-hidden transition-colors resize-none"
                      placeholder="Skriv din besked her..."
                    />
                  </div>

                  {/* Error Message */}
                  {status === "error" && (
                    <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                      <i className="fa-solid fa-circle-exclamation mr-2" />
                      {errorMessage}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex w-full sm:w-auto items-center justify-center rounded-full bg-brand-600 px-8 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus:outline-hidden focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 transition-all duration-200 hover:scale-102 active:scale-98 cursor-pointer"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin mr-2" />
                        Sender...
                      </>
                    ) : (
                      "Send besked"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Embed */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl border border-brand-100 hover:border-brand-500/50 shadow-xs hover:shadow-md h-[450px] transition-all duration-500">
          <iframe
            title="Kiropraktisk Klinik Assens"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d80136.71041309777!2d9.834578753309334!3d55.29097029477385!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464cc7c153b0e6cb%3A0xd3b42cc4582f678d!2sKiropraktisk%20Klinik%20Assens!5e0!3m2!1sen!2sdk!4v1781694088173!5m2!1sen!2sdk"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-full grayscale opacity-85 hover:grayscale-0 hover:opacity-100 transition-all duration-700 ease-in-out"
          />
        </div>
      </section>
    </div>
  );
}
